import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Animated, Keyboard, Platform, ScrollView, StyleSheet} from 'react-native';
import {useReducedMotion} from 'react-native-reanimated';

import SignUpEmailScreenContent from './SignUpEmailScreenContent';
import SignUpPasswordScreenContent from './SignUpPasswordScreenContent';

/**
 * What the caller's account lookup found for the address that was entered.
 *
 * One member per status the account lookup can return, so a new status cannot
 * be silently folded into an existing branch.
 */
export type SignUpEmailOutcome = 'existing' | 'new' | 'rewardsSignUp' | 'unavailable';

export type SignUpFlowStep = 'email' | 'password';

export interface SignUpFlowContentProps {
  /**
   * Account lookup for the entered address. Returning 'existing' advances to
   * the password step; 'new' hands the address back through onNewAccount.
   * Injected rather than called here so this component stays presentational.
   */
  onCheckEmail: (email: string) => Promise<SignUpEmailOutcome> | SignUpEmailOutcome;
  /** The address is not registered — the caller owns the create-account path. */
  onNewAccount: (email: string) => void;
  /**
   * The address belongs to a rewards member who has not finished signing up.
   * A separate journey owns that, so the flow hands the address over and stops.
   */
  onRewardsSignUp: (email: string) => void;
  /** Credentials for an existing account, submitted from the password step. */
  onLogIn: (email: string, password: string) => void;
  onForgotPassword: (email: string) => void;
  /** Close icon on the email step. The back arrow returns here instead. */
  onClose: () => void;
  initialEmail?: string;
  /** Error copy for the password field, e.g. a rejected password. */
  passwordErrorMessage?: string;
  /** Error copy for the email field, e.g. a lookup that could not complete. */
  emailErrorMessage?: string;
}

/**
 * Hosts the two sign-up entry screens and the transition between them.
 *
 * Entering an address that the injected onCheckEmail reports as 'existing'
 * cross-fades to the password screen; the back arrow cross-fades back. Both
 * screens stay presentational — this component owns only step state, the
 * field values, and the animation.
 *
 * Uses the React Native Animated API rather than Reanimated's entering/exiting
 * animations: Reanimated 4.5 needs patches/react-native-reanimated+4.5.0.patch
 * to animate at all under this repo's webpack web build, and npm 12 skips the
 * postinstall that applies it. Only useReducedMotion is taken from Reanimated,
 * and that hook reads AccessibilityInfo rather than driving an animation.
 */
const SignUpFlowContent = ({
  onCheckEmail,
  onNewAccount,
  onRewardsSignUp,
  onLogIn,
  onForgotPassword,
  onClose,
  initialEmail = '',
  passwordErrorMessage,
  emailErrorMessage,
}: SignUpFlowContentProps) => {
  const [step, setStep] = useState<SignUpFlowStep>('email');
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  // 'out' while the current screen is leaving, 'in' while the next one arrives.
  const [phase, setPhase] = useState<'in' | 'out'>('in');

  const progress = useRef(new Animated.Value(0)).current;
  // 1 while advancing to the password step, -1 while going back.
  const direction = useRef(1);
  const inFlight = useRef(false);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    progress.setValue(0);
    const animation = Animated.timing(progress, {
      toValue: 1,
      duration: FADE_IN_MS,
      useNativeDriver: USE_NATIVE_DRIVER,
    });
    animation.start();
    return () => animation.stop();
  }, [step, progress]);

  const transitionTo = useCallback(
    (nextStep: SignUpFlowStep, nextDirection: 1 | -1) => {
      // The next step focuses a different field, and on Android the open
      // keyboard would otherwise sit over the arriving screen mid-transition.
      Keyboard.dismiss();
      direction.current = nextDirection;
      setPhase('out');
      Animated.timing(progress, {
        toValue: 0,
        duration: FADE_OUT_MS,
        useNativeDriver: USE_NATIVE_DRIVER,
      }).start(({finished}) => {
        if (!finished) {
          return;
        }
        setStep(nextStep);
        setPhase('in');
      });
    },
    [progress],
  );

  const onNext = useCallback(async () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    try {
      const outcome = await onCheckEmail(email);
      if (outcome === 'existing') {
        transitionTo('password', 1);
      } else if (outcome === 'rewardsSignUp') {
        onRewardsSignUp(email);
      } else if (outcome === 'new') {
        onNewAccount(email);
      }
      // 'unavailable': the lookup did not complete and the caller has put the
      // reason on emailErrorMessage. Stay on the email step.
    } finally {
      inFlight.current = false;
    }
  }, [email, onCheckEmail, onNewAccount, onRewardsSignUp, transitionTo]);

  const onBack = useCallback(() => {
    setPassword('');
    transitionTo('email', -1);
  }, [transitionTo]);

  // The screen leaving and the screen arriving travel the same way, so the
  // outgoing offset is the opposite sign of the incoming one.
  const offset = reducedMotion ? 0 : SLIDE_PX * direction.current * (phase === 'out' ? -1 : 1);

  const animatedStyle = {
    opacity: progress,
    transform: [
      {
        translateX: progress.interpolate({inputRange: [0, 1], outputRange: [offset, 0]}),
      },
    ],
  };

  const stepContent =
    step === 'email' ? (
      <SignUpEmailScreenContent
        email={email}
        onChangeEmail={setEmail}
        onNext={onNext}
        onClose={onClose}
        errorMessage={emailErrorMessage}
      />
    ) : (
      <SignUpPasswordScreenContent
        email={email}
        password={password}
        onChangePassword={setPassword}
        onLogIn={() => onLogIn(email, password)}
        onBack={onBack}
        onForgotPassword={() => onForgotPassword(email)}
        errorMessage={passwordErrorMessage}
      />
    );

  return (
    <Animated.View style={[styles.stage, animatedStyle]} testID="signUpFlow">
      {/* DIS-1859: on mobile web the soft keyboard shrinks the viewport, and
          without a scroll container NEXT is clipped and unreachable. Native is
          left as it was. */}
      {Platform.OS === 'web' ? (
        <ScrollView
          style={styles.stage}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          testID="signUpFlowScroll">
          {stepContent}
        </ScrollView>
      ) : (
        stepContent
      )}
    </Animated.View>
  );
};

export default SignUpFlowContent;

// react-native-web ships no native animated module, so asking for the native
// driver there only logs a fallback warning on every transition.
const USE_NATIVE_DRIVER = Platform.OS !== 'web';
const FADE_OUT_MS = 120;
const FADE_IN_MS = 180;
const SLIDE_PX = 12;

const styles = StyleSheet.create({
  stage: {
    flex: 1,
  },
  // Lets the step's flex: 1 screen fill a short page and scroll a tall one.
  scrollContent: {
    flexGrow: 1,
  },
});
